import { useState } from "react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiBuildHouseAlt } from "@miplatform/assets";
import {
  MiBox,
  MiButton,
  MiDetailFields,
  MiIcon,
  MiPlaceholderArea,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  DOCUMENT_TYPES,
  INPUT_TYPES,
} from "@miplatform/constants";
import { IDocumentListItemResponse } from "@miplatform/types";
import { createUUID, externalReferenceId } from "@miplatform/utils";
import { useDocumentsViewModel } from "@miplatform/view-models";
import { mdiCheck, mdiClose, mdiMinusCircle, mdiPlus } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { UploadDocumentModal } from "../../../components/UploadDocumentModal/UploadDocumentModal";
import { InputFile } from "../../CreateBid/BidDetailsForm/components/InputFile/InputFile";
import { DocCategory } from "../../CreateBid/CreateBids.types";
import { DOC_CATEGORY } from "../../CreateBid/constants";
import {
  CreateBidRequestFormData,
  bidCreateStatuses,
} from "../CreateBidRequest.type";
import { BID_CREATE_STATUS } from "../constants/constants";

export function BidsSection({
  form,
  open,
  bidStatuses,
  setBidStatuses,
  handleViewFile,
}: Readonly<BidsSectionProps>) {
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [activeBidIndex, setActiveBidIndex] = useState<number | null>(null);
  const [docCategory, setDocCategory] = useState<DocCategory | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentLabel, setDocumentLabel] = useState("");

  const { classes, theme } = useStyles();
  const {
    loading: docLoading,
    uploadDocument,
    removeDocument,
  } = useDocumentsViewModel();
  const { control, setValue, watch } = form;
  const { bids = [] } = watch();
  const { fields, append, remove } = useFieldArray({ control, name: "bids" });

  if (!open) {
    return null;
  }

  const handleOpenUploadModal = (index: number, category: DocCategory) => {
    setActiveBidIndex(index);
    setDocCategory(category);
    setDocumentLabel(category === DOC_CATEGORY.OTHER ? "" : category);
    setSelectedFile(null);
    setOpenUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setActiveBidIndex(null);
    setDocCategory(null);
    setSelectedFile(null);
    setDocumentLabel("");
    setOpenUploadModal(false);
  };

  const handleUploadFile = async () => {
    if (!selectedFile || !docCategory || activeBidIndex === null) return;
    const category =
      docCategory === DOC_CATEGORY.OTHER ? documentLabel : docCategory;
    const { documents, bidId } = bids[activeBidIndex];
    const uploadedDoc = await uploadDocument({
      file: selectedFile,
      documentType: DOCUMENT_TYPES.UNKNOWN,
      category,
      externalReferenceId: externalReferenceId.getBidRefId({ bidId }),
    });
    if (!uploadedDoc) return;
    const addedDoc = [...documents, uploadedDoc] as IDocumentListItemResponse[];
    setValue(`bids.${activeBidIndex}.documents`, addedDoc, {
      shouldValidate: true,
      shouldDirty: true,
    });
    handleCloseUploadModal();
  };

  const handleRemoveFile = async (
    index: number,
    doc: IDocumentListItemResponse
  ) => {
    const success = await removeDocument(doc);
    if (!success) return;
    const { documents } = bids[index];
    const removedDoc = documents.filter((d) => d.id !== doc.id);
    setValue(`bids.${index}.documents`, removedDoc, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const getBidFields = (index: number) => {
    const bid = bids[index];
    const planDoc = bid.documents.find((d) => d.category === DOC_CATEGORY.PLAN);
    const specDoc = bid.documents.find((d) => d.category === DOC_CATEGORY.SPEC);
    const otherDocs = bid.documents.filter(
      (d) =>
        d.category !== DOC_CATEGORY.PLAN && d.category !== DOC_CATEGORY.SPEC
    );

    const createStatus = bidStatuses[bid?.bidId];
    const isSuccessful = createStatus === BID_CREATE_STATUS.SUCCESS;
    const isFailed = createStatus === BID_CREATE_STATUS.FAILED;

    return [
      {
        fieldType: INPUT_TYPES.CUSTOM,
        minWidth: 0,
        flexGrow: 0,
        width: "fit-content",
        style: { paddingTop: theme.spacing(1.6) },
        customComponent: (
          <Typography className={classes.rowIndex}>{index + 1}</Typography>
        ),
      },
      {
        fieldType: INPUT_TYPES.CUSTOM,
        minWidth: 0,
        flexGrow: 0,
        width: "fit-content",
        style: { paddingTop: theme.spacing(1.6) },
        customComponent: (
          <Box>
            {isSuccessful && (
              <MiIcon
                path={mdiCheck}
                size={0.7}
                color={theme.palette.success.main}
              />
            )}
            {isFailed && (
              <MiIcon
                path={mdiClose}
                size={0.7}
                color={theme.palette.error.main}
              />
            )}
          </Box>
        ),
      },
      {
        label: "Bid Name",
        name: `bids.${index}.name`,
        width: "20%",
        formControl: control,
        required: true,
        disabled: isSuccessful,
        helperText: isFailed ? "Bid name already exists." : "",
        error: isFailed,
        setValue: (e: React.ChangeEvent<HTMLInputElement>) => {
          setValue(`bids.${index}.name`, e.target.value, {
            shouldValidate: true,
          });
          if (isFailed) {
            setBidStatuses((prev) => ({ ...prev, [bid.bidId]: undefined }));
          }
        },
      },
      {
        fieldType: INPUT_TYPES.CUSTOM,
        width: "20%",
        formControl: control,
        name: `bids.${index}.planFile`,
        customComponent: (
          <InputFile
            doc={planDoc}
            handleRemoveFile={(doc) => handleRemoveFile(index, doc)}
            handleOpenUploadModal={() =>
              handleOpenUploadModal(index, DOC_CATEGORY.PLAN)
            }
            handleViewFile={handleViewFile}
            label="Upload Plan File"
            required
            disabled={isSuccessful}
          />
        ),
      },
      {
        fieldType: INPUT_TYPES.CUSTOM,
        width: "20%",
        formControl: control,
        name: `bids.${index}.specFile`,
        customComponent: (
          <InputFile
            doc={specDoc}
            handleRemoveFile={(doc) => handleRemoveFile(index, doc)}
            handleOpenUploadModal={() =>
              handleOpenUploadModal(index, DOC_CATEGORY.SPEC)
            }
            handleViewFile={handleViewFile}
            label="Upload Specification File"
            required
            disabled={isSuccessful}
          />
        ),
      },
      {
        fieldType: INPUT_TYPES.CUSTOM,
        width: "20%",
        formControl: control,
        name: `bids.${index}.otherFile`,
        customComponent: (
          <InputFile
            doc={otherDocs[0]}
            handleRemoveFile={(doc) => handleRemoveFile(index, doc)}
            handleOpenUploadModal={() =>
              handleOpenUploadModal(index, DOC_CATEGORY.OTHER)
            }
            handleViewFile={handleViewFile}
            label="Upload Other File"
            disabled={isSuccessful}
          />
        ),
      },
      {
        fieldType: INPUT_TYPES.CUSTOM,
        minWidth: 0,
        flexGrow: 0,
        width: "fit-content",
        style: { paddingTop: theme.spacing(1.6) },
        customComponent: (
          <MiButton
            startIcon={{ path: mdiMinusCircle }}
            color={BUTTON_COLORS.ERROR}
            disabled={isSuccessful}
            onClick={() => remove(index)}
            className={classes.deleteBtn}
          />
        ),
      } as any,
    ];
  };

  return (
    <>
      <MiBox
        headerTitle="Bids"
        headerAction={
          <MiButton
            title="Add Bid"
            variant={BUTTON_VARIANTS.CONTAINED}
            startIcon={{ path: mdiPlus }}
            onClick={() =>
              append({ bidId: createUUID(), name: "", documents: [] })
            }
          />
        }
        sx={{ marginTop: 0 }}
      >
        <MiPlaceholderArea
          open={fields.length === 0}
          text="No Bids Created"
          helperText="Tip: To create, click the + Add Bid button"
          iconComponent={<img src={MiBuildHouseAlt} alt="House Icon" />}
          containerStyle={{
            backgroundColor: theme.palette.primary.light,
            margin: theme.spacing(1),
          }}
        />
        {fields.map((field, index) => (
          <MiDetailFields
            key={field.id}
            detailFields={getBidFields(index)}
            customWrapperStyle={{ display: "flex", alignItems: "flex-start" }}
          />
        ))}
      </MiBox>
      <UploadDocumentModal
        open={openUploadModal}
        docCategory={docCategory}
        documentLabel={documentLabel}
        selectedFile={selectedFile}
        loading={docLoading}
        handleCloseUploadModal={handleCloseUploadModal}
        handleUploadFile={handleUploadFile}
        setDocumentLabel={setDocumentLabel}
        setSelectedFile={setSelectedFile}
      />
    </>
  );
}

const useStyles = makeStyles()((theme) => ({
  rowIndex: {
    fontWeight: theme.fontWeight.semiBold,
    color: theme.palette.grey.medium,
    fontSize: theme.fontSize.medium,
  },
  deleteBtn: {
    borderRadius: "50%",
    padding: theme.spacing(0),
  },
}));

interface BidsSectionProps {
  form: UseFormReturn<CreateBidRequestFormData>;
  open: boolean;
  bidStatuses: bidCreateStatuses;
  setBidStatuses: React.Dispatch<React.SetStateAction<bidCreateStatuses>>;
  handleViewFile: (doc?: IDocumentListItemResponse | null) => void;
}
